import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClipboardList,
  Users,
  BarChart3,
  CheckCircle,
  ArrowRight,
} from 'lucide-react';

import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const SourcingLanding = () => {
  const navigate = useNavigate();

  const sourcingCards = [
    {
      id: 'rfps',
      title: 'RFP Management',
      description:
        'Create, publish and manage sourcing requests with invited vendors and deadlines.',
      icon: <ClipboardList size={24} />,
      path: '/sourcing/rfps',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: [
        'Draft & publish RFPs',
        'Invite vendors',
        'Manage response deadlines',
      ],
    },
    {
      id: 'responses',
      title: 'Vendor Responses',
      description:
        'Track supplier submissions, pricing details and attachments in one place.',
      icon: <Users size={24} />,
      path: '/sourcing/responses',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: [
        'Supplier submissions',
        'Line-item pricing',
        'Attachment review',
      ],
    },
    {
      id: 'evaluation',
      title: 'Bid Evaluation',
      description:
        'Score technical and commercial bids to identify the best vendor.',
      icon: <BarChart3 size={24} />,
      path: '/sourcing/bid-evaluation',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: [
        'Technical scoring',
        'Commercial comparison',
        'Weighted results',
      ],
    },
    {
      id: 'awards',
      title: 'Awards',
      description:
        'Approve vendor selections and seamlessly initiate contract creation.',
      icon: <CheckCircle size={24} />,
      path: '/sourcing/awards',
      iconBg: '#D6EFFF',
      iconLine: 'var(--gold-primary)',
      features: [
        'Award approvals',
        'Vendor selection',
        'Create contract from award',
      ],
    },
  ];

  const handleCardClick = (path) => {
    navigate(path);
  };

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />

      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader
        title="Strategic Sourcing"
        description="Manage the complete request-to-award lifecycle from vendor invitation to contract initiation."
      />

      <div className="page-container">
        {/* Cards */}
        <div
          className="grid-modern-3"
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            width: '100%',
          }}
        >
          {sourcingCards.map((card) => (
            <div
              key={card.id}
              className="asset-landing-card"
              onClick={() => handleCardClick(card.path)}
            >
              {/* Top Accent Line */}
              <div
                style={{
                  height: '4px',
                  background: `var(--gold-primary)`,
                  width: '100%',
                }}
              />

              {/* Card Content */}
              <div style={{ padding: '24px' }}>
                {/* Icon and Title */}
                <div className="d-flex align-items-center mb-3">
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      background: card.iconBg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '16px',
                    }}
                  >
                    <div style={{ color: card.iconLine || 'var(--gold-primary)' }} className="asset-card-icon">
                      {card.icon}
                    </div>
                  </div>
                  <div className="flex-grow-1">
                    <h4
                      style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        color: 'var(--text-primary)',
                        margin: '0',
                        fontFamily: 'var(--font-family)',
                      }}
                    >
                      {card.title}
                    </h4>
                  </div>
                  <ArrowRight size={20} className="asset-card-arrow" />
                </div>

                {/* Description */}
                <p
                  style={{
                    fontSize: '13px',
                    color: 'var(--text-secondary)',
                    margin: '0 0 12px 0',
                    lineHeight: '1.4',
                    fontFamily: 'var(--font-family)',
                  }}
                >
                  {card.description}
                </p>

                {/* Features */}
                <div>
                  <div
                    style={{
                      fontSize: '11px',
                      fontWeight: '500',
                      color: '#374151',
                      marginBottom: '6px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Key Features
                  </div>
                  <div className="d-flex flex-wrap gap-1">
                    {card.features.map((feature, index) => (
                      <span
                        key={index}
                        style={{
                          fontSize: '12px',
                          padding: '4px 8px',
                          background: '#f3f4f6',
                          color: '#374151',
                          borderRadius: '6px',
                          fontWeight: '500',
                        }}
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default SourcingLanding;